// Ventana de «app» del SO Orion: glifo con el degradado de la org, título, subtítulo,
// pestañas (opcionales) y acciones. Misma gramática que Deals/Leads; reúsala en vez de
// armar cabeceras a mano.

import type { ComponentType, ReactNode } from 'react';
import './os-app.css';

export function OsApp({ icon: Icon, title, subtitle, tabs, actions, children, className = '' }: {
  icon: ComponentType<{ className?: string; strokeWidth?: number; style?: React.CSSProperties }>;
  title: string;
  subtitle?: ReactNode;
  tabs?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`os-app flex w-full flex-col text-foreground ${className}`}>
      <div className="orion-card flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <header className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5 sm:px-4">
          <div className="flex min-w-0 flex-1 items-center gap-2.5 sm:flex-none">
            <span aria-hidden className="os-glyph grid h-9 w-9 shrink-0 place-items-center rounded-xl">
              <Icon style={{ width: 19, height: 19 }} strokeWidth={2.2} />
            </span>
            <div className="min-w-0">
              <h1 className="text-[16px] font-semibold leading-tight">{title}</h1>
              {subtitle && <p className="truncate text-[12px] tabular-nums text-muted-foreground">{subtitle}</p>}
            </div>
          </div>
          {tabs}
          {actions && <div className="flex items-center gap-2 sm:ml-auto">{actions}</div>}
        </header>
        <div className="border-t border-border bg-background/40 p-3 sm:p-4">{children}</div>
      </div>
    </div>
  );
}

/** Pestañas segmentadas (fondo muted, la activa como tarjeta). */
export function OsTabs<T extends string>({ value, onChange, items, label, fullWidthOnMobile = true }: {
  value: T;
  onChange: (v: T) => void;
  items: { key: T; label: string; icon?: ComponentType<{ className?: string }> }[];
  label: string;
  fullWidthOnMobile?: boolean;
}) {
  return (
    <nav role="tablist" aria-label={label}
      className={`flex items-center gap-1 rounded-xl bg-muted p-1 ${fullWidthOnMobile ? 'order-3 w-full sm:order-none sm:w-auto' : ''}`}>
      {items.map(({ key, label: l, icon: I }) => (
        <button key={key} type="button" role="tab" aria-selected={value === key} onClick={() => onChange(key)}
          className={`inline-flex h-8 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-2 text-[12.5px] font-medium transition-colors sm:flex-none sm:px-3 sm:text-[13px] ${
            value === key ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
          {I && <I className="h-4 w-4" />}{l}
        </button>
      ))}
    </nav>
  );
}

export const osActionBtn =
  'inline-flex h-9 items-center gap-1.5 rounded-xl border border-border bg-card px-3 text-[13px] font-medium text-foreground hover:bg-muted disabled:opacity-50';
